import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAddInStore, useTokenStore } from "../../../hooks";
import { msGraphService, supabaseApi } from "../../../services";
import { logger } from "../../../utils";
import { Space } from "../../../models";
import {
  Body1Strong,
  Button,
  Caption2,
  Divider,
  InfoLabel,
  Input,
  Label,
  List,
  ListItem,
  makeStyles,
  Select,
  SelectionItemId,
  Tag,
  Text,
  tokens,
} from "@fluentui/react-components";
import { useOfficeItem } from "../../../providers";
import { first, intersection, set, uniq } from "lodash";
import { isEmail } from "../../../utils/helpers";
import { PersonAddRegular } from "@fluentui/react-icons";

const useStyles = makeStyles({
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  list_item: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: "5px 0",
  },
  list_item_text: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  list_header: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  list_actions: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "end",
    alignItems: "center",
    gap: "5px",
    "> button": {
      padding: "0px",
      margin: "0px",
      minWidth: "0px",
      color: tokens.colorBrandForegroundLink,
      "&:disabled": {
        color: tokens.colorNeutralForegroundDisabled,
      },
    },
  },
  list_actions_divider: {
    width: "5px",
    padding: "0px",
    margin: "0px",
  },
  add_member_control: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "5px",
  },
  add_member_control_input: {
    flex: "1 auto",
    "> input": {
      width: "100%",
    },
  },
});

export interface SpaceAttendeeSelectListProps {
  spaceId?: string;
  meetingCreatedBy?: string;
  selectAllSpaceMembersByDefault?: boolean;
}

export const SpaceAttendeeSelectList = ({
  spaceId,
  meetingCreatedBy,
  selectAllSpaceMembersByDefault = true,
}: SpaceAttendeeSelectListProps) => {
  // used to control component styles
  const styles = useStyles();

  // indicates the current event and function to update attendees
  const { officeService, event } = useOfficeItem();

  // indicates the meeting organizers email address
  const [organizer, setOrganizer] = useState<string | undefined>(undefined);

  // indicates the member being added to our member list
  const [addMemberInputValue, setAddMemberInputValue] = useState<string | undefined>(undefined);

  // indicates the members associated with the selected space
  const [members, setMembers] = useState<string[]>([]);

  // indicates the selected members to add to the meeting
  const [selectedMembers, setSelectedMembers] = useState<SelectionItemId[]>([]);

  // indicates the current auth state
  const realtimeAttendees = useMemo(() => event?.attendees, [event?.attendees]);

  /**
   * Updates the member list when a new member is selected or removed.
   *
   * @param {string[]} items - the list of selected members
   * @returns {Promise<void>}
   * @throws {Error} - if there is an error updating the member list
   */
  const onMemberSelectionChanged = useCallback(
    async (items: string[]) => {
      try {
        /**  get the current required members list */
        const currentRequiredMembers = await officeService.attendees("required");

        /** map list of list of Office.EmailAddressDetails to email strings */
        const currentRequiredMembersEmails = currentRequiredMembers.map(
          (member) => member.emailAddress
        );

        /** find all the members that are being removed */
        const removedMembers = selectedMembers
          .filter((member) => !items.includes(member.toString()))
          .map((member) => member.toString());

        /** find all the members that are being added */
        const addedMembers = items.filter(
          (member) => !currentRequiredMembersEmails.includes(member)
        );

        /** get a list of all the required attendees that we need update in our list */
        const updatedMemberList = currentRequiredMembersEmails?.filter(
          (member) => !removedMembers.includes(member)
        );

        /** update the event with the latest members */
        await officeService.setAttendees("required", uniq([...updatedMemberList, ...addedMembers]));

        /** update the local list of selected members */
        await setSelectedMembers(items);
      } catch (err) {
        // TODO: show and handle error here
        logger.error("Error fetching space members", err);
      }
    },
    [selectedMembers]
  );

  const onAddMemberSubmit = async (evt) => {
    evt.preventDefault();

    /** make sure this member doesn't already exist */
    const foundMember = members.findIndex((member) => member === addMemberInputValue);

    /** if they don't add them to the members list */
    if (foundMember === -1) {
      setMembers((prevMembers) => uniq([...prevMembers, addMemberInputValue]));
    }

    /** update the selected members */
    await onMemberSelectionChanged(
      uniq([...selectedMembers.map((x) => x.toString()), addMemberInputValue])
    );

    /** reset the form input */
    setAddMemberInputValue(undefined);
  };

  useEffect(() => {
    if (!spaceId) {
      setMembers([]);
      return;
    }

    const getSpaceMembers = async () => {
      try {
        setMembers([]);

        // get the current required attendees from the event
        const currentAttendees = await officeService.attendees("required");

        // get the space members
        const response = await supabaseApi.getSpaceMembers(spaceId);
        if (response) {
          // get unique emails from the response
          const memberEmails = uniq(
            response
              .map((attendee) => attendee?.user_profile?.login_email ?? "")
              .filter((email) => email !== "")
          );

          const attendees = uniq([
            ...memberEmails,
            ...currentAttendees.map((attendee) => attendee.emailAddress),
          ]);

          if (meetingCreatedBy) {
            const organizerEmail = first(
              response.filter((attendee) => attendee.user_profile.id === meetingCreatedBy)
            );
            setOrganizer(organizerEmail?.user_profile?.login_email);
          }

          // update members list
          setMembers(attendees);

          // add existing attendees to the selected members list
          if (currentAttendees?.length > 0 && !selectAllSpaceMembersByDefault) {
            const attendeesToPreSelect = uniq(
              intersection(
                memberEmails,
                currentAttendees.map((attendee) => attendee.emailAddress)
              )
            );
            setSelectedMembers(attendeesToPreSelect);
            onMemberSelectionChanged(attendeesToPreSelect);
          } else {
            setSelectedMembers(attendees);
            onMemberSelectionChanged(attendees);
          }
        }
      } catch (err) {
        logger.error("Error fetching space members", err);
        setMembers([]);
      }
    };

    getSpaceMembers();
  }, [spaceId, meetingCreatedBy, selectAllSpaceMembersByDefault, event?.id]);

  useEffect(() => {
    if (!realtimeAttendees || !Array.isArray(realtimeAttendees)) return;

    setSelectedMembers((prevSelected) => {
      // Convert all to string to avoid type mismatch
      const attendeeSet = new Set(realtimeAttendees.map(String));
      const prevSelectedSet = new Set(prevSelected.map(String));

      // Step 1: Add missing attendees
      const added = realtimeAttendees.filter((email) => !prevSelectedSet.has(email));

      // Step 2: Remove members no longer in the list
      const kept = prevSelected.filter((email) => attendeeSet.has(email.toString()));

      return uniq([...kept, ...added]);
    });

    setMembers((prevMembers) => {
      const memberSet = new Set(prevMembers);
      const newMembers = realtimeAttendees.filter((email) => !memberSet.has(email));
      return uniq([...prevMembers, ...newMembers]);
    });
  }, [realtimeAttendees]);

  return (
    <div className={styles.list}>
      <div className={styles.list_header}>
        <InfoLabel
          size="medium"
          weight="semibold"
          info={
            event?.id
              ? "Members in the list below are already invited to this event."
              : "Members in the list below will be added to the CADChat meeting."
          }
        >
          Members
        </InfoLabel>
        <div className={styles.list_actions}>
          <Button
            appearance="transparent"
            size="small"
            onClick={() => onMemberSelectionChanged(members.map((member) => member))}
            disabled={selectedMembers.length === members.length}
          >
            Select all
          </Button>

          <Divider vertical />

          <Button
            appearance="transparent"
            size="small"
            onClick={() => onMemberSelectionChanged([])}
            disabled={!selectedMembers.length}
          >
            Clear
          </Button>
        </div>
      </div>

      <List
        selectionMode="multiselect"
        selectedItems={selectedMembers}
        onSelectionChange={(_, data) =>
          onMemberSelectionChanged(data.selectedItems.map((item) => item.toString()))
        }
        aria-label="Meeting Members"
      >
        {members.map((member) => (
          <ListItem
            key={member}
            value={member}
            aria-label={member}
            checkmark={{ "aria-label": member }}
            className={styles.list_item}
          >
            <Text className={styles.list_item_text}>
              {member}{" "}
              {member === organizer ? (
                <Tag size="small" shape="circular" appearance="brand">
                  organizer
                </Tag>
              ) : null}
            </Text>
          </ListItem>
        ))}
      </List>

      <form
        onSubmit={onAddMemberSubmit}
        key="add-member-list-item"
        className={styles.add_member_control}
      >
        <Input
          id="add-member-input"
          placeholder="Add Member"
          className={styles.add_member_control_input}
          value={addMemberInputValue ?? ""}
          type="email"
          onChange={(_, data) => setAddMemberInputValue(data.value)}
        />
        <Button
          type="submit"
          icon={<PersonAddRegular />}
          disabled={!isEmail(addMemberInputValue)}
          title="Add a member to this meeting"
        >
          Add
        </Button>
      </form>
    </div>
  );
};
